import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Tag, Trash2, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";

const AdminDiscounts = () => {
  const { authHeaders } = useOutletContext();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    code: "",
    percent: 10,
    max_uses: 100,
    expires_at: "",
  });
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/discounts`, { headers: authHeaders })
      .then((r) => setCodes(r.data))
      .catch(() => toast.error("Laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [authHeaders]);

  const create = async (e) => {
    e.preventDefault();
    const pct = Number(form.percent);
    if (Number.isNaN(pct) || pct < 1 || pct > 25) {
      toast.error("Korting moet tussen 1% en 25% liggen (Alcoholwet — max 25%).");
      return;
    }
    if (!form.code.trim()) {
      toast.error("Vul een code in.");
      return;
    }
    setCreating(true);
    try {
      const payload = {
        code: form.code.trim().toUpperCase(),
        percent: pct,
        max_uses: Number(form.max_uses),
        expires_at: form.expires_at
          ? new Date(form.expires_at + "T23:59:59Z").toISOString()
          : null,
      };
      await axios.post(`${API}/admin/discounts`, payload, { headers: authHeaders });
      toast.success(`Code ${payload.code} aangemaakt`);
      setForm({ code: "", percent: 10, max_uses: 100, expires_at: "" });
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Aanmaken mislukt";
      toast.error(typeof msg === "string" ? msg : "Aanmaken mislukt");
    } finally {
      setCreating(false);
    }
  };

  const toggle = async (code) => {
    try {
      await axios.patch(`${API}/admin/discounts/${code}/toggle`, {}, { headers: authHeaders });
      load();
    } catch {
      toast.error("Wijziging mislukt");
    }
  };

  const remove = async (code) => {
    if (!confirm(`Code ${code} verwijderen?`)) return;
    try {
      await axios.delete(`${API}/admin/discounts/${code}`, { headers: authHeaders });
      toast.success("Verwijderd");
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div data-testid="admin-discounts-page" className="space-y-6">
      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
          <Plus size={14} className="text-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-widest text-white">NIEUWE KORTINGSCODE</span>
        </div>
        <form onSubmit={create} className="p-5 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">CODE</label>
            <input
              data-testid="new-discount-code"
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              className="w-full mt-1 px-3 py-2 text-sm font-mono-x"
              placeholder="BV. FAMILIE10"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">% (MAX 25)</label>
            <input
              data-testid="new-discount-percent"
              required
              type="number"
              min="1"
              max="25"
              value={form.percent}
              onChange={(e) => setForm({ ...form, percent: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">MAX GEBRUIK</label>
            <input
              data-testid="new-discount-max-uses"
              required
              type="number"
              min="1"
              value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">EINDDATUM (OPT)</label>
            <input
              data-testid="new-discount-expires"
              type="date"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              className="w-full mt-1 px-3 py-2 text-sm"
            />
          </div>
          <div className="sm:col-span-5">
            <button
              data-testid="create-discount-button"
              disabled={creating}
              className="btn-red px-6 py-3 font-display tracking-wider text-sm disabled:opacity-60"
            >
              {creating ? "AANMAKEN..." : "CODE AANMAKEN"}
            </button>
            <p className="mt-2 text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
              MAX 25% KORTING — CONFORM ALCOHOLWET (NL, 1 JULI 2021)
            </p>
          </div>
        </form>
      </div>

      <div className="border border-[var(--tha-border)] bg-[#0a0a0a]">
        <div className="px-5 py-4 border-b border-[var(--tha-border)] flex items-center gap-2">
          <Tag size={14} className="text-[var(--tha-red)]" />
          <span className="text-xs font-mono-x tracking-widest text-white">ACTIEVE & VERVALLEN CODES</span>
          <span className="ml-auto text-xs text-[var(--tha-muted)]">{codes.length} totaal</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-[var(--tha-muted)]">Laden...</div>
        ) : codes.length === 0 ? (
          <div className="p-10 text-center text-sm text-[var(--tha-muted)]">Nog geen kortingscodes.</div>
        ) : (
          <ul>
            {codes.map((c) => (
              <li
                key={c.code}
                className="px-5 py-4 border-b border-[var(--tha-border)] last:border-0 flex flex-wrap items-center gap-4"
              >
                <div className="font-mono-x text-lg text-[var(--tha-red)]">{c.code}</div>
                <div className="text-sm text-white">{c.percent}% korting</div>
                <div className="text-xs text-[var(--tha-muted)]">
                  {c.uses}/{c.max_uses} gebruikt
                </div>
                {c.expires_at && (
                  <div className="text-xs text-[var(--tha-muted)]">
                    t/m {new Date(c.expires_at).toLocaleDateString("nl-NL")}
                  </div>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <span
                    className={`text-[10px] font-mono-x tracking-widest px-2 py-1 ${
                      c.active
                        ? "bg-[#22c55e22] text-[#22c55e]"
                        : "bg-[#33333322] text-[var(--tha-muted)]"
                    }`}
                  >
                    {c.active ? "ACTIEF" : "INACTIEF"}
                  </span>
                  <button
                    data-testid={`toggle-discount-${c.code}`}
                    onClick={() => toggle(c.code)}
                    className="btn-ghost-red p-2"
                    aria-label="Toggle"
                  >
                    <Power size={12} />
                  </button>
                  <button
                    data-testid={`delete-discount-${c.code}`}
                    onClick={() => remove(c.code)}
                    className="btn-ghost-red p-2"
                    aria-label="Verwijder"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminDiscounts;
